import {SortDirection} from 'src/openapi/admin/models/SortDirection';

import {GRANT_STATUS_CAPTIONS} from '../constants';

import type {Preset, ResourceView} from '../typedef';
import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {PresetItem} from 'src/openapi/admin/models/PresetItem';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';
import type {Role} from 'src/openapi/admin/models/Role';


type GetPresetsListProps = {
    relations: PresetRelationItem[],
    presets: Record<string, PresetItem>,
    resources: Record<string, EntitlementResource>,
    roles: Record<string, Role>,
    grants: Record<string, Grant>,

    sortDirection: SortDirection,
};
export type GetPresetsList = (props: GetPresetsListProps) => ResourceView[];

export const getPresetsList: GetPresetsList = ({
    presets,
    roles,
    grants,
    resources,
    relations,
    sortDirection,
}) => {
    const view: ResourceView[] = [];

    const presetsMap: Record<string, Preset> = {};

    relations.forEach(({resourceId, grantId, roleId, presetId, level}) => {
        let resourceView: ResourceView;
        if (!resourceId) {
            resourceView = {
                recordId: JSON.stringify({presetId, isEmpty: true}),

                title: presets[presetId].name,
                description: presets[presetId].description,
                level,
            };

            view.push(resourceView);
            return;
        } else {
            resourceView = {
                recordId: '',

                title: resources[resourceId].name,
                description: resources[resourceId].description,

                grantTitle: grants[grantId].name,
                statusTitle: GRANT_STATUS_CAPTIONS[grants[grantId].status],
                level,
                restrictionTitle: grants[grantId].daysToDelay !== null
                    ? `DaysToDelay: ${grants[grantId].daysToDelay}`
                    : '--',
            };
        }

        if (!presetsMap[presetId]) {
            presetsMap[presetId] = {
                assignedResources: {},
                freeResources: [],
            };
        }

        if (!roleId) {
            presetsMap[presetId].freeResources.push({
                ...resourceView,
                recordId: JSON.stringify({resourceId, grantId, presetId}),
            });
        } else if (!presetsMap[presetId].assignedResources[roleId]) {
            presetsMap[presetId].assignedResources[roleId] = [];
            presetsMap[presetId].assignedResources[roleId].push({
                ...resourceView,
                recordId: JSON.stringify({resourceId, grantId, roleId, presetId}),
            });
        } else {
            presetsMap[presetId].assignedResources[roleId].push({
                ...resourceView,
                recordId: JSON.stringify({resourceId, grantId, roleId, presetId}),
            });
        }
    });

    const sortFunc = (a: string, b: string) => {
        return sortDirection === SortDirection.ASC ? a.localeCompare(b) : b.localeCompare(a);
    };

    const buildPreset = (presetId: string) => {
        view.push({
            recordId: JSON.stringify({presetId}),
            title: presets[presetId].name,
            description: presets[presetId].description,
        });

        Object
            .keys(roles)
            .sort((a, b) => sortFunc(roles[a].name, roles[b].name))
            .forEach(roleId => {
                const assignedResourceViews = presetsMap[presetId].assignedResources[roleId];

                if (assignedResourceViews) {
                    const level = relations.find(rel => roleId === rel.roleId)?.level;
                    view.push({
                        recordId: JSON.stringify({roleId, presetId}),
                        title: `${roles[roleId].name} (${assignedResourceViews.length})`,
                        description: roles[roleId].description,
                        level,
                    });

                    view.push(...assignedResourceViews
                        .sort((a, b) => sortFunc(a.title, b.title)),
                    );
                }
            });

        view.push(
            ...presetsMap[presetId].freeResources.sort((a, b) => sortFunc(a.title, b.title)),
        );
    };

    Object
        .keys(presets)
        .filter(presetId => presetsMap[presetId])
        .sort((a, b) => sortFunc(presets[a].name, presets[b].name))
        .forEach(buildPreset);

    return view;
};
